import { useEffect, useState } from 'react';
import type { StockClient } from '@/infrastructure/StockClient';

export type StockSkuLookup = Pick<StockClient, 'getProductBySku'>;
export const SKU_LOOKUP_TIMEOUT_MS = 10_000;

/** Catalog SKUs are 6 to 14 ASCII digits (9- and 10-digit SKUs both exist in production); never repair malformed input. */
export const stockSkuFormatError = (sku: string): string | null => {
  if (!sku) {
    return 'SKU is required.';
  }
  if (!/^\d+$/.test(sku)) {
    return 'SKU must be numbers only.';
  }
  return /^\d{6,14}$/.test(sku) ? null : 'SKU must be 6 to 14 digits.';
};

/** Fail closed: stock rows are not proof that a SKU exists in the catalog. */
export const validateStockSku = async (
  client: StockSkuLookup,
  input: string,
  signal?: AbortSignal,
): Promise<string | null> => {
  const sku = input.trim();
  const formatError = stockSkuFormatError(sku);
  if (formatError) {
    return formatError;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    if (signal?.aborted) {
      throw new Error('SKU lookup canceled');
    }
    const product = await Promise.race([
      client.getProductBySku(sku, controller.signal),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('SKU lookup timed out'));
        }, SKU_LOOKUP_TIMEOUT_MS);
      }),
    ]);
    return product?.sku === sku && product.productId > 0 ? null : `No product or variation with SKU ${sku}.`;
  } catch {
    return `Could not verify SKU ${sku}. Check the SKU and try again.`;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
};

/** A result belongs only to the current draft; late answers cannot enable a different SKU. */
export const useStockSkuValidation = (client: StockSkuLookup, input: string) => {
  const sku = input.trim();
  const [result, setResult] = useState<{ input: string; error: string | null } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setResult(null);
    if (!stockSkuFormatError(sku)) {
      void validateStockSku(client, sku, controller.signal).then((error) => {
        if (current) {
          setResult({ input, error });
        }
      });
    }
    return () => {
      current = false;
      controller.abort();
    };
  }, [client, input, sku, attempt]);

  const checked = result?.input === input ? result : null;
  const error = stockSkuFormatError(sku) ?? checked?.error ?? null;
  return {
    valid: Boolean(checked) && !error,
    invalid: Boolean(input) && Boolean(error),
    message: input ? (error ?? (!checked ? `Checking SKU ${sku}…` : null)) : null,
    retry: () => {
      setResult(null);
      setAttempt((value) => value + 1);
    },
    canRetry: Boolean(checked?.error),
  };
};
